import React from 'react';
import { CircularProgress } from '@mui/material';
import { useColumnHeaderContext } from '../hooks/useColumnHeaderContext';

interface ColumnHeaderMenuListProps {
  emptyStateMessage?: string;
}

export const ColumnHeaderMenuList: React.FC<ColumnHeaderMenuListProps> = ({ emptyStateMessage = 'No values found' }) => {
  const { items, filter, search, customFilterSlot } = useColumnHeaderContext();

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!items.onLoadMore || !items.hasMore || items.isLoadingMore || items.isLoading) return;
    const target = event.currentTarget;
    const hasReachedBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 24;
    if (hasReachedBottom) {
      items.onLoadMore();
    }
  };

  return (
    <>
      {/* Custom Filter Slot - Consumer controls everything */}
      {customFilterSlot && <div className='px-8 py-12 border-b border-neutral-20'>{customFilterSlot}</div>}

      {/* Loading State (Initial) */}
      {items.isLoading ? (
        <div className='flex items-center justify-center py-12'>
          <CircularProgress size={24} />
        </div>
      ) : (
        <>
          {items?.items?.length > 0 ? (
            <div className='overflow-y-auto py-12 max-h-[200px]' onScroll={handleScroll}>
              {items.items.map((item) => (
                <div
                  key={`filtered-${item.id}`}
                  className='flex items-center py-8 px-10 hover:bg-neutral-10 cursor-pointer w-full'
                  role='checkbox'
                  aria-checked={filter.selectedValues.includes(item.id)}
                  tabIndex={0}
                >
                  <div className='flex items-center justify-between gap-12 w-full' role='presentation'>
                    <div className='flex items-center gap-8'>
                      <div className='relative flex items-center'>
                        <input
                          type='checkbox'
                          id={`filter-${item.id}`}
                          checked={filter.selectedValues.includes(item.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            filter.onToggleValue(item.id);
                          }}
                          className='h-18 w-18 rounded-4 border-neutral-40 accent-[#00992B] cursor-pointer'
                          style={{ accentColor: '#00992B' }}
                        />
                      </div>
                      <label htmlFor={`filter-${item.id}`} className='ml-12 text-xs cursor-pointer flex items-center gap-10'>
                        {item.label === null || item.label === 'null' || item.label === '' ? '-' : item?.label}

                        {/* Generic Badge */}
                        {item.badge && (
                          <span
                            className='text-[10px] py-2 px-4 font-semibold rounded-6'
                            style={{
                              backgroundColor: item.badge.bgColor || '#F2F4F7',
                              color: item.badge.textColor || '#737373',
                            }}
                          >
                            {item.badge.label}
                          </span>
                        )}
                      </label>
                    </div>
                    {item.count !== undefined && item.count > 0 && <span className='text-xs text-neutral-60'>{item.count}</span>}
                  </div>
                </div>
              ))}

              {/* Empty State */}
              {!items.isLoading && items.items.length === 0 && (
                <div className='flex items-center justify-center py-12 text-xs text-neutral-60'>{emptyStateMessage}</div>
              )}

              {/* Loading More (Pagination) */}
              {items.isLoadingMore && (
                <div className='flex items-center justify-center py-12'>
                  <CircularProgress size={20} />
                </div>
              )}
            </div>
          ) : (
            <div className='py-16 px-12'>
              <p className='text-14 text-neutral-60'>No values found</p>
            </div>
          )}
        </>
      )}
    </>
  );
};
